import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, Store, Truck, Heart, MapPin, Clock, Phone, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { ASSETS, STORE_INFO, MEDIA } from "@/lib/assets";
import { StoreStatusBadge } from "@/components/StoreStatusBadge";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="px-3 pt-3 sm:px-5 lg:px-8">
        <div className="relative mx-auto max-w-[1440px] overflow-hidden rounded-[2rem] bg-forest lg:rounded-[2.5rem]">
          <div className="pattern-dots absolute inset-0" />
          <img src={ASSETS.logoMark} alt="" aria-hidden="true" className="pointer-events-none absolute -right-16 top-1/2 hidden h-[130%] w-auto -translate-y-1/2 opacity-[0.12] md:block" />
          <div className="relative z-10 flex min-h-[56vh] max-w-3xl flex-col justify-center px-6 py-20 sm:px-10 lg:px-16">
            <motion.span
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="label-eyebrow inline-block w-fit rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-ochre backdrop-blur-sm"
            >
              Our story
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 font-display text-5xl font-semibold leading-[0.98] tracking-tight text-white lg:text-7xl"
            >
              A market with <span className="italic text-ochre">soul</span>.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-sand/85"
            >
              Bambam African Mart began with a simple longing — for the aromas, colours and flavours of home. Today we bring authentic African groceries to Calgary kitchens while keeping our neighbourhood store at the heart of it all.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link to="/shop" className="group inline-flex items-center gap-2 rounded-full bg-clay px-7 py-4 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:bg-[#A3161F] active:scale-95">
                Explore the mart <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a href="#visit-store" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-4 text-sm font-bold text-white backdrop-blur-sm transition-colors hover:bg-white/15">
                Visit the store
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <img src={ASSETS.logoMark} alt="" aria-hidden="true" className="mx-auto mb-4 h-16 w-auto" />
          <span className="label-eyebrow text-clay">What we stand for</span>
          <h2 className="mt-3 font-display text-4xl font-semibold tracking-tight text-forest lg:text-5xl">More than a grocery run</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Leaf, t: "Authentic", s: "Sourced from trusted producers across the continent." },
            { icon: Store, t: "Community", s: "A physical home our neighbourhood knows and loves." },
            { icon: Truck, t: "Delivered", s: "Same-day to your door across Calgary." },
            { icon: Heart, t: "With care", s: "Packed by people who cook these dishes too." },
          ].map((v, i) => (
            <motion.div
              key={v.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-[1.75rem] border border-border/70 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(15,74,35,0.3)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-leaf/10 text-forest">
                <v.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-forest">{v.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.s}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="px-3 sm:px-5 lg:px-8">
        <div className="mx-auto grid max-w-[1440px] gap-3 lg:grid-cols-2">
          <div className="relative flex flex-col justify-center overflow-hidden rounded-[2rem] bg-forest px-6 py-16 text-sand sm:px-12 lg:py-20">
            <div className="pattern-dots absolute inset-0" />
            <div className="relative">
              <span className="label-eyebrow text-ochre">In-store meets online</span>
              <h2 className="mt-4 font-display text-4xl font-semibold leading-tight text-white lg:text-5xl">
                Rooted in Africa, growing in Calgary.
              </h2>
              <p className="mt-5 max-w-xl text-sand/80">
                Every item on this website is the very same stock on our shelves. Buy online or walk the aisle — our inventory stays in perfect sync, so you always get exactly what you came for.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  { icon: Truck, label: "Same-day delivery in Calgary" },
                  { icon: Store, label: "Free in-store pickup" },
                  { icon: ShieldCheck, label: "Fresh from our shelves" },
                ].map((s) => (
                  <span key={s.label} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white">
                    <s.icon className="h-4 w-4 text-ochre" /> {s.label}
                  </span>
                ))}
              </div>
              <Link to="/shop" className="mt-9 inline-flex items-center gap-2 rounded-full bg-ochre px-7 py-4 text-sm font-bold text-ink transition-all duration-200 hover:bg-white active:scale-95">
                Start shopping <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="relative min-h-[360px] overflow-hidden rounded-[2rem]">
            <img src={MEDIA.feast} srcSet={`${MEDIA.feastSm} 700w, ${MEDIA.feast} 1400w`} sizes="(min-width:1024px) 50vw, 100vw" alt="Jollof rice, egusi soup, suya and zobo" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* VISIT OUR STORE */}
      <section data-testid="visit-store" id="visit-store" className="mx-auto max-w-[1440px] scroll-mt-32 px-5 py-20 lg:px-10">
        <div className="grid items-stretch gap-8 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={ASSETS.storefront} alt="Bambam African Mart storefront in Calgary" className="h-full min-h-[320px] w-full rounded-[2rem] object-cover shadow-[0_30px_60px_-30px_rgba(15,74,35,0.4)]" />
          </motion.div>
          <div className="flex flex-col justify-center rounded-[2rem] border border-border/70 bg-white p-8 lg:p-10">
            <span className="label-eyebrow text-clay">Visit us</span>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h2 className="font-display text-4xl font-semibold tracking-tight text-forest lg:text-5xl">Our Calgary store</h2>
              <StoreStatusBadge />
            </div>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-clay" /><span className="text-muted-foreground"><span className="font-semibold text-forest">{STORE_INFO.address}</span><br />{STORE_INFO.city}</span></li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
                <div className="w-full">
                  <p className="mb-1 font-semibold text-forest">Opening hours</p>
                  <div className="space-y-0.5">
                    {STORE_INFO.hours.map((h) => (
                      <div key={h.d} className="flex justify-between gap-4 text-muted-foreground">
                        <span>{h.d}</span><span>{h.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-clay" /><span className="text-muted-foreground">{STORE_INFO.phones.join(" · ")}</span></li>
              <li className="flex items-start gap-3"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-clay" /><a href={`mailto:${STORE_INFO.email}`} className="text-muted-foreground hover:text-clay">{STORE_INFO.email}</a></li>
            </ul>
            <div className="mt-6 overflow-hidden rounded-2xl border border-border">
              <iframe
                title="Bambam African Mart location"
                src={STORE_INFO.mapEmbed}
                className="h-56 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
